import api from './api';
import config from '../config/setup';

// Cache for birdsview data to avoid multiple API calls
let birdsviewCache = null;
let birdsviewCacheDate = null;

// Helper function to get today's date in YYYY-MM-DD format in UTC
const getTodayDateString = () => {
    const now = new Date();
    return now.toISOString().split('T')[0];
};

// Helper function for logging
const log = (message, data) => {
    if (config.logging.enableConsole) {
        console.log(message, data);
    }
};

export const getBirdsviewData = async (date = null) => {
    try {
        const targetDate = date || getTodayDateString();
        log('Fetching birdsview data for date:', targetDate, 'at:', new Date().toISOString());
        
        // Check if we have cached data for the same date
        if (birdsviewCache && birdsviewCacheDate === targetDate) {
            log('Using cached birdsview data for date:', targetDate);
            return birdsviewCache;
        }
        
        const response = await api.get(`/api/manager/birdsview?date=${targetDate}`);
        birdsviewCache = response.data;
        birdsviewCacheDate = targetDate;
        return response.data;
    } catch (error) {
        console.error('Error fetching birdsview data:', error);
        throw error;
    }
};

export const getMemberTasks = async (memberId, date = null) => {
    try {
        const targetDate = date || getTodayDateString();
        
        // If we don't have cached data for the target date, fetch it
        if (!birdsviewCache || birdsviewCacheDate !== targetDate) {
            await getBirdsviewData(targetDate);
        }

        // Find the member's data
        const memberData = birdsviewCache.find(member => member.id === memberId);
        if (!memberData) {
            throw new Error('Member not found');
        }

        // If member has no plan for the target date, return empty array
        if (!memberData.hasTodaysPlan || !memberData.planId) {
            return [];
        }

        // Get the work plan summary which includes tasks
        log('Fetching tasks for date:', targetDate, 'at:', new Date().toISOString());
        const response = await api.get(`/api/manager/summary/${memberId}/${targetDate}`);
        return response.data.tasks || [];
    } catch (error) {
        console.error('Error fetching member tasks:', error);
        throw error;
    }
};

export const getWorkPlanSummary = async (userId, date) => {
    try {
        const response = await api.get(`/api/manager/summary/${userId}/${date}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching work plan summary:', error);
        throw error;
    }
}; 

export const getMemberWorkPlanHistory = async (userId) => {
    try {
        const response = await api.get(`/api/manager/history/${userId}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching work plan history:', error);
        throw error;
    }
}; 

export const getYesterdaySummary = async () => {
    try {
        log('Fetching yesterday summary data at:', new Date().toISOString());
        const response = await api.get('/api/manager/yesterday-summary');
        return response.data;
    } catch (error) {
        console.error('Error fetching yesterday summary data:', error);
        throw error;
    }
}; 

export const getTimeOffsData = async (year, month) => {
    try {
        const response = await api.get(`/api/manager/time-offs/${year}/${month}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching time-offs data:', error);
        throw error;
    }
};

export const getTaskNotifications = async (taskId) => {
    try {
        log('Fetching task notifications for task:', taskId);
        const response = await api.get(`/api/manager/task-notifications/${taskId}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching task notifications:', error);
        throw error;
    }
};

export const getTaskNotificationsBatch = async (taskIds) => {
    try {
        log('Fetching task notifications batch for tasks:', taskIds);
        const response = await api.post('/api/manager/task-notifications-batch', {
            taskIds: taskIds
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching task notifications batch:', error);
        throw error;
    }
};

export const getTaskConversationCountsBatch = async (taskIds) => {
    try {
        log('Fetching manager discussion counts for tasks:', taskIds);
        const response = await api.post('/api/manager/conversations/batch-counts', {
            taskIds: taskIds
        });
        return response.data?.counts || {};
    } catch (error) {
        console.error('Error fetching conversation counts batch:', error);
        throw error;
    }
};

export const getPeterConversations = async (filters = {}) => {
    try {
        const { userId, status, type, startDate, endDate } = filters;
        const params = new URLSearchParams();
        
        if (userId) params.append('userId', userId);
        if (status) params.append('status', status);
        if (type) params.append('type', type);
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);
        
        const queryString = params.toString();
        const url = `/api/manager/peter-conversations${queryString ? `?${queryString}` : ''}`;
        
        log('Fetching Peter conversations with filters:', filters);
        const response = await api.get(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching Peter conversations:', error);
        throw error;
    }
}; 